import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { GL_ACTIONS } from '@usf/ngrx-gl';
import { INVENTORY_ITEM_ACTIONS } from '@usf/ngrx-inventory';
import {
  getProductPriceUpdateNumber,
  NON_USF_PRODUCTS,
  NonUSFProductActions,
} from '@usf/ngrx-product';
import { combineLatest } from 'rxjs';
import {
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CustomerStoreService } from '../../../../ngrx-customer/services';
import { DownloadPrintBaseService } from '../../../../shared/services/document/download-print-base.service';
import { NonUsfProductViewModelSelectors } from '../selectors/non-usf-selectors';
import { ManageNonUsfProductsService } from '../services/manage-non-usf-products.service';
import { Store } from '@ngrx/store';
import { RECIPE_ACTIONS } from '@usf/ngrx-tools';

@Injectable({
  providedIn: 'root',
})
export class ManageNonUSFProductsEffects {
  createNonUSFSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.createSuccess),
        tap(action => {
          this.dispalySuccessToast('i18n.nonUsfProducts.createSuccessMessage');
        }),
      ),
    { dispatch: false },
  );

  createNonUSFFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.createFail),
        tap(action => {
          this.displayFailToast('i18n.nonUsfProducts.createFailureMessage');
        }),
      ),
    { dispatch: false },
  );

  updateNonUSFSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.updateSuccess),
        tap(action => {
          this.dispalySuccessToast('i18n.nonUsfProducts.updateSuccessMessage');
        }),
        withLatestFrom(this.store.select(getProductPriceUpdateNumber())),
        switchMap(([action, productNumber]) => {
          let productCasePrice = -1;
          if (action.nonUSFProduct.isPricePerUnitInd) {
            productCasePrice =
              action.nonUSFProduct.price *
              (action.nonUSFProduct.unitsPerCase ?? 1);
          } else {
            productCasePrice = action.nonUSFProduct.price;
          }
          const actions = [
            INVENTORY_ITEM_ACTIONS.nonUSFValueUpdate({
              items: [
                {
                  nonUsfProductNumber: action.nonUSFProduct.productNumber,
                  productPackSizeLabel: action.nonUSFProduct.salesPackSize,
                  productCasePrice,
                  productConvFactor: action.nonUSFProduct.unitsPerCase,
                  productUnitOfMeasure: action.nonUSFProduct.priceUom,
                },
              ],
            }),
          ];
          if (
            !!productNumber &&
            action.nonUSFProduct.productNumber === productNumber
          ) {
            const cost = action.nonUSFProduct.isPricePerUnitInd
              ? action.nonUSFProduct.price * action.nonUSFProduct.unitsPerCase
              : action.nonUSFProduct.price;
            actions.push(
              RECIPE_ACTIONS.updateCostIngredient({
                costUpdateDto: {
                  costUpdateItem: [
                    {
                      id: action.nonUSFProduct.productNumber.toString(),
                      cost,
                      productYield: action.nonUSFProduct.productYield ?? 1,
                      unitOfMeasure: action.nonUSFProduct.unitOfMeasure,
                      referencedRecipeList:
                        action.nonUSFProduct.referencedRecipeList ?? [],
                      referencedMenuItemList:
                        action.nonUSFProduct.referencedMenuItemList ?? [],
                      referencedEventPlanList:
                        action.nonUSFProduct.referencedEventPlanList ?? [],
                    },
                  ],
                },
              }),
              NonUSFProductActions.resetUpdateFlag(),
            );
          }

          return actions;
        }),
      ),
    { dispatch: true },
  );

  updateInventoryFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.updateFail),
        tap(action => {
          this.displayFailToast('i18n.nonUsfProducts.updateFailureMessage');
        }),
      ),
    { dispatch: false },
  );

  deleteNonUSFSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.deleteSuccess),
        tap(action => {
          this.dispalySuccessToast('i18n.nonUsfProducts.deleteSuccessMessage');
        }),
      ),
    { dispatch: false },
  );

  deleteNonUSFFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.deleteFail),
        tap(action => {
          this.displayFailToast('i18n.nonUsfProducts.deleteFailureMessage');
        }),
      ),
    { dispatch: false },
  );

  downloadGlCodeCsv$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GL_ACTIONS.downloadGlCodeCsv),
        switchMap(action =>
          combineLatest([
            this.nonUsfProductViewModelSelectors.glCodeCsv$,
            this.customerStoreService.loadSelectedCustomer$(),
          ]).pipe(take(1)),
        ),
        tap(([glCodeCsv, customer]) => {
          const fileName = `GL Data_${customer.customerNumber}`;
          this.downloadPrintBaseService.saveDocument(
            glCodeCsv,
            fileName,
            'CSV',
            true,
          );
        }),
      ),
    { dispatch: false },
  );

  importNonUSFSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonUSFProductActions.importSuccess),
      tap(action => {
        if (action.response.errorDetails.length > 0) {
          this.manageNonUsfProductsService.showImportNonUsfProductSuccessToast(
            true,
            action.response.errorDetails,
          );
        } else {
          this.dispalySuccessToast(
            'i18n.inventory.importNonUsfProdcutsModal.importSuccessMessage',
          );
        }
      }),
      mergeMap(action => [
        GL_ACTIONS.loadGlCodes(),
        INVENTORY_ITEM_ACTIONS.nonUSFValueUpdate({
          items: action.response.nonUsfProductProcessed.map(item => {
            let productCasePrice = -1;
            if (item.isPricePerUnitInd) {
              productCasePrice = item.price * (item.unitsPerCase ?? 1);
            } else {
              productCasePrice = item.price;
            }
            return {
              nonUsfProductNumber: item.productNumber,
              productCasePrice,
              productConvFactor: item.unitsPerCase,
              productPackSizeLabel: item.salesPackSize,
              productUnitOfMeasure: item.priceUom,
            };
          }),
        }),
      ]),
    ),
  );

  importNonUSFFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.importFail),
        tap(action => {
          this.displayFailToast(
            'i18n.inventory.importNonUsfProdcutsModal.importFailureMessage',
          );
        }),
      ),
    { dispatch: false },
  );

  displayFailToast(message: string): void {
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.error,
      display: this.translateService.instant(message),
      toast: true,
      sticky: true,
    } as Message);
  }

  dispalySuccessToast(message: string): void {
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.success,
      display: this.translateService.instant(message),
      toast: true,
      sticky: false,
    } as Message);
  }

  constructor(
    readonly store: Store,
    private actions$: Actions,
    private translateService: TranslateService,
    private messageStoreService: MessageStoreService,
    private customerStoreService: CustomerStoreService,
    private downloadPrintBaseService: DownloadPrintBaseService,
    private nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    readonly manageNonUsfProductsService: ManageNonUsfProductsService,
  ) {}
}
